import React, { useEffect, useMemo, useState } from 'react';

import { ScrollText, Volume2, WandSparkles, X } from 'lucide-react';

import { EChatSource } from '../../../../types/entities.enum';
import {
    IConversationForAnalysisReport,
    IMSARMBTSRapportCollapse,
    IMSARMBTSRapportCollapsesConversation,
    IMSARMBTSReport,
} from '../../../../types/services.interfaces/mbts-ux';

import { ConsentLock } from '../ConsentLock';

import { SECTION_IDS, getElapsedLabel } from './utils';

interface InteractionLabProps {
    report: IMSARMBTSReport;
    conversations: IConversationForAnalysisReport[];
    isConsentLocked: boolean;
    openConsentDataGather: () => void;
};

type TurnItem = {
    collapse: IMSARMBTSRapportCollapse;
    session: IMSARMBTSRapportCollapsesConversation;
    elapsed: string;
};

export const InteractionLab = (props: InteractionLabProps) => {
    const { report, conversations, isConsentLocked, openConsentDataGather } = props;

    const turns = useMemo<TurnItem[]>(() => {
        return report.rapport_collapses_conversations.flatMap(session =>
            session.rapport_collapses.map(collapse => ({
                collapse,
                session,
                elapsed: getElapsedLabel(session.session_start, collapse.chat.created_at),
            }))
        );
    }, [report.rapport_collapses_conversations]);

    const [activeIdx, setActiveIdx] = useState(0);
    const [transcriptOpen, setTranscriptOpen] = useState(false);
    const [personaFilter, setPersonaFilter] = useState<string | null>(null);

    const personas = useMemo(() => {
        const map = new Map<string, string>();
        report.rapport_collapses_conversations.forEach(session => {
            map.set(session.conversation.persona_id, session.conversation.persona.title);
        });
        return Array.from(map.entries());
    }, [report.rapport_collapses_conversations]);

    const displayTurns = useMemo(() => {
        if (!personaFilter) return turns;
        return turns.filter(t => t.session.conversation.persona_id === personaFilter);
    }, [turns, personaFilter]);

    useEffect(() => {
        setActiveIdx(0);
    }, [personaFilter, displayTurns.length]);

    const displayActive = displayTurns[activeIdx] ?? null;

    const transcript = useMemo(() => {
        if (!displayActive) return null;
        return conversations.find(c => c.id === displayActive.session.conversation_id) ?? null;
    }, [displayActive, conversations]);

    const speak = (text: string) => {
        if (!text || !('speechSynthesis' in window)) return;
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        window.speechSynthesis.speak(utterance);
    };

    return (
        <section id={SECTION_IDS.lab} className="mbts_analysis_v2__section mbts_analysis_v2__lab">
            <ConsentLock isConsentLocked={isConsentLocked} openConsentDataGather={openConsentDataGather} />

            <div className="mbts_analysis_v2__section__head">
                <div>
                    <div className="mbts_analysis_v2__section__title_row">
                        <span className="mbts_analysis_v2__section__dot mbts_analysis_v2__section__dot--error mbts_analysis_v2__section__dot--pulse" />
                        <h3 className="mbts_analysis_v2__section__title">Interaction Heatmap & Response Lab</h3>
                    </div>
                    <p className="mbts_analysis_v2__section__subtitle">
                        Navigate through key dialogue turns to hear how tone impacted stakeholder rapport.
                    </p>
                </div>
                <button
                    type="button"
                    className="mbts_analysis_v2__lab__transcript_btn"
                    onClick={() => setTranscriptOpen(true)}
                    disabled={!displayActive || !transcript}
                >
                    <ScrollText />
                    <span>Full Simulation Transcript</span>
                </button>
            </div>

            {personas.length > 1 && (
                <div className="mbts_analysis_v2__lab__personas">
                    <button
                        type="button"
                        className={`mbts_analysis_v2__lab__persona_btn${!personaFilter ? ' mbts_analysis_v2__lab__persona_btn--active' : ''}`}
                        onClick={() => setPersonaFilter(null)}
                    >
                        All
                    </button>
                    {personas.map(([id, title]) => (
                        <button
                            key={id}
                            type="button"
                            className={`mbts_analysis_v2__lab__persona_btn${personaFilter === id ? ' mbts_analysis_v2__lab__persona_btn--active' : ''}`}
                            onClick={() => setPersonaFilter(id)}
                        >
                            {title[0].toUpperCase() + title.slice(1).replace(/-/g, ' ')}
                        </button>
                    ))}
                </div>
            )}

            {!displayTurns.length && (
                <p className="mbts_analysis_v2__lab__empty">No friction turns were identified for this simulation.</p>
            )}

            {!!displayTurns.length && displayActive && (
                <>
                    <div className="mbts_analysis_v2__lab__timeline">
                        <div className="mbts_analysis_v2__lab__timeline__top">
                            <span className="mbts_analysis_v2__lab__timeline__label">Interaction Timeline</span>
                            <span className="mbts_analysis_v2__lab__timeline__meta">
                                Turn {activeIdx + 1} of {displayTurns.length}
                                <span>Friction Point @ {displayActive.elapsed}</span>
                            </span>
                        </div>
                        <div className="mbts_analysis_v2__lab__turns">
                            {displayTurns.map((turn, idx) => (
                                <button
                                    key={`${turn.collapse.id}-${idx}`}
                                    type="button"
                                    className={`mbts_analysis_v2__lab__turn${idx === activeIdx ? ' mbts_analysis_v2__lab__turn--active' : ''}`}
                                    onClick={() => setActiveIdx(idx)}
                                >
                                    <span>{(idx + 1).toString().padStart(2, '0')}</span>
                                    <span>{turn.elapsed}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mbts_analysis_v2__lab__compare">
                        <div className="mbts_analysis_v2__lab__panel mbts_analysis_v2__lab__panel--yours">
                            <div className="mbts_analysis_v2__lab__panel__head">
                                <span className="mbts_analysis_v2__lab__panel__badge">Your Response</span>
                                <button
                                    type="button"
                                    className="mbts_analysis_v2__lab__listen"
                                    onClick={() => speak(displayActive.collapse.chat.message)}
                                >
                                    <Volume2 />
                                    <span>Listen</span>
                                </button>
                            </div>
                            <blockquote>&quot;{displayActive.collapse.chat.message}&quot;</blockquote>
                            <div className="mbts_analysis_v2__lab__panel__note">
                                <span>Impact Analysis</span>
                                <p>{displayActive.collapse.impact_analysis}</p>
                            </div>
                        </div>

                        <div className="mbts_analysis_v2__lab__panel mbts_analysis_v2__lab__panel--rec">
                            <div className="mbts_analysis_v2__lab__panel__head">
                                <span className="mbts_analysis_v2__lab__panel__badge">
                                    <WandSparkles />
                                    Recommended Alternative
                                </span>
                                <button
                                    type="button"
                                    className="mbts_analysis_v2__lab__listen"
                                    onClick={() => speak(displayActive.collapse.recommended_alternative)}
                                >
                                    <Volume2 />
                                    <span>Listen</span>
                                </button>
                            </div>
                            <blockquote>&quot;{displayActive.collapse.recommended_alternative}&quot;</blockquote>
                            <div className="mbts_analysis_v2__lab__panel__note">
                                <span>Strategic Shift</span>
                                <p>{displayActive.collapse.strategic_shift}</p>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {transcriptOpen && displayActive && transcript && (
                <div className="mbts_analysis_v2__modal" role="dialog" aria-modal="true">
                    <div className="mbts_analysis_v2__modal__backdrop" onClick={() => setTranscriptOpen(false)} />
                    <div className="mbts_analysis_v2__modal__panel">
                        <div className="mbts_analysis_v2__modal__head">
                            <div>
                                <h3>Full Simulation Transcript</h3>
                                <p>
                                    {transcript.persona.title} ({transcript.persona.person})
                                </p>
                            </div>
                            <button type="button" onClick={() => setTranscriptOpen(false)} aria-label="Close transcript">
                                <X />
                            </button>
                        </div>
                        <div className="mbts_analysis_v2__modal__body">
                            {transcript.chats.map(chat => {
                                const isCritical = chat.id === displayActive.collapse.chat_id;
                                const isYou = chat.source !== EChatSource.PERSONA;
                                const identity = isYou
                                    ? `${report.player_current_role} (You)`
                                    : `${transcript.persona.title} (${transcript.persona.person})`;

                                return (
                                    <div
                                        key={chat.id}
                                        className={`mbts_analysis_v2__modal__msg${isYou ? ' mbts_analysis_v2__modal__msg--you' : ''}${isCritical ? ' mbts_analysis_v2__modal__msg--critical' : ''}`}
                                    >
                                        <div className="mbts_analysis_v2__modal__msg__meta">
                                            <strong>
                                                {identity} ({getElapsedLabel(displayActive.session.session_start, chat.created_at)})
                                            </strong>
                                            {isCritical && <span>Friction Point</span>}
                                        </div>
                                        <p>{chat.message}</p>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mbts_analysis_v2__modal__foot">
                            <button type="button" onClick={() => setTranscriptOpen(false)}>
                                Close Transcript
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};
